<template>
  <form-label-group
    :label="calcLabel"
    :class="[
      'phone-number-field',
      'mb-2',
      { 'phone-number-field--filled': hasValue, 'phone-number-field--focused': isFocused },
    ]"
  >
    <phone-number-input
      ref="field"
      :value="innerValue"
      :id="id"
      :name="name"
      :disabled="disabled"
      :required="required"
      :preferred-countries="preferredCountries"
      :default-country-code="defaultCountry"
      :no-use-browser-locale="!shouldDetectContry"
      no-validator-state
      no-example
      @phone-number-focused="onFocus"
      @phone-number-blur="onBlur"
      @input="onInput"
      @update="onUpdate"
    />
  </form-label-group>
</template>

<script>
import FormLabelGroup from '@/components/organisation/FormLabelGroup';

import getRef from '@/utils/getRef';

const languageMap = {
  sv: 'SE',
  en: 'GB',
};

export default {
  name: 'PhoneNumberField',

  components: { FormLabelGroup },

  props: {
    value: {
      type: [String, Number],
      default: null,
    },
    id: {
      type: String,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
    preferredCountries: {
      type: Array,
      default: () => ['SE', 'GB', 'US'],
    },
    required: {
      type: Boolean,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: null,
    },
  },

  data() {
    return {
      innerValue: null,
      outputValue: this.value,
      isFocused: false,
      touched: false,
    };
  },

  computed: {
    hasValue() {
      return !!this.innerValue;
    },

    shouldDetectContry() {
      return !this.touched && !this.hasValue;
    },

    defaultCountry() {
      return this.shouldDetectContry ? this.getCountryCodeByLocale(this.$i18n.locale()) : null;
    },

    calcLabel() {
      if (!this.label) {
        return;
      }

      return this.required ? `${this.label} *` : this.label;
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(next) {
        this.innerValue = next;
      },
    },
  },

  mounted() {
    this.$nextTick(() => {
      const ref = this.getInputRef();
      if (ref) {
        ref.placeholder = '';
      }
    });
  },

  methods: {
    getFieldRef() {
      return getRef(this.$refs, 'field');
    },

    getPhoneRef() {
      let ref = this.getFieldRef();
      if (ref) {
        ref = getRef(ref.$refs, 'PhoneNumberInput');
      }

      return ref;
    },

    getInputRef() {
      let ref = this.getPhoneRef();
      if (ref) {
        ref = getRef(ref.$refs, 'InputTel');
      }

      return ref;
    },

    getCountryCodeByLocale(locale) {
      /**
       * @todo i18n and phone number util's locales are different,
       * but for now we only have two locales to manage so it should be ok
       **/
      return locale ? languageMap[locale] || locale.toUpperCase() : null;
    },

    // Used by parent components
    setLocaleIfEmpty(locale) {
      const ref = this.getFieldRef();
      if (ref && !ref.countryCode && ref.setLocale) {
        const countryCode = this.getCountryCodeByLocale(locale);
        ref.setLocale(countryCode);
      }
    },

    onFocus() {
      this.isFocused = true;
    },

    onBlur() {
      this.isFocused = false;
      this.$emit('input', this.outputValue);
    },

    onInput(value) {
      this.touched = true;
      this.innerValue = value;
    },

    onUpdate(value) {
      this.touched = true;
      if (value.formatNational || !value.phoneNumber) {
        this.$nextTick(() => {
          this.innerValue = value.formatNational;
        });
      }
      this.outputValue = value.formatInternational;
      this.$emit('update', this.outputValue);
    },
  },
};
</script>

<style scoped>
.phone-number-field {
  font-size: 1rem;
}

/** @todo vue-phone-number-input lib's css is compiled and expects to find it's processed image by direct url. */
.phone-number-field >>> .iti-flag {
  background-image: url('/static/img/flags.png') !important;
}

.phone-number-field >>> .select-country-container {
  flex: 0 1 auto;
  width: 100px;
  min-width: auto;
  max-width: 100px;
}

.phone-number-field >>> .vue-phone-number-input > :not(.select-country-container) {
  flex: 1 0 auto;
}

.phone-number-field >>> .select-country-container .country-selector__input {
  padding: 0 0 0 38px;
  border-top-left-radius: 0.3rem !important;
  border-bottom-left-radius: 0.3rem !important;
  border: none;
  border-right: 1px solid #ccc !important;
  box-shadow: none !important;
}

.phone-number-field >>> .select-country-container button {
  color: #212529;
}

.phone-number-field >>> .country-selector label {
  display: none;
}

.phone-number-field >>> .country-selector .country-selector__country-flag {
  top: 50%;
  transform: translateY(-50%) scale(1.2);
}

.phone-number-field >>> .select-country-container .country-selector__toggle {
  top: 50%;
  transform: translateY(-50%);
}

.phone-number-field >>> .vue-phone-number-input ~ label {
  padding-left: 106px;
}

.phone-number-field >>> .country-selector,
.phone-number-field >>> .country-selector__input,
.phone-number-field >>> .input-tel,
.phone-number-field >>> .input-tel__input {
  height: 48px;
}

.phone-number-field >>> .input-tel__label {
  display: none;
}

.phone-number-field >>> .input-tel.is-focused {
  z-index: auto;
}

.phone-number-field--filled >>> .vue-phone-number-input ~ label {
  padding-top: calc(var(--input-padding-y) / 3);
  padding-bottom: calc(var(--input-padding-y) / 3);
  font-size: 12px;
  color: #777;
}

.phone-number-field >>> .input-tel__input {
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  caret-color: #212529 !important;
  border-top-right-radius: 0.3rem !important;
  border-bottom-right-radius: 0.3rem !important;
  margin-left: 0;
  border: none;
  padding: 4px 12px;
  box-shadow: none !important;
}

.phone-number-field {
  border: 1px solid #ced4da;
  border-radius: 0.3rem;
}

.phone-number-field--focused {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
}
</style>



// WEBPACK FOOTER //
// src/components/form/PhoneNumberField.vue